import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface ActivateUserModalProps {
  isOpen: boolean
  onClose: () => void
  employee: {
    id: number
    employeeNumber: string
    firstName: string
    lastName: string
    email: string
    employmentStatus: string
  }
  onConfirm: (userId: number) => void | Promise<void>   // ✅ ajouté
  isLoading?: boolean                                   // ✅ ajouté
}



interface ActivationResponse {
  success: boolean
  message: string
  credentials?: {
    tempPassword: string
    expiresAt: string
  }
}

const ActivateUserModal: React.FC<ActivateUserModalProps> = ({
  isOpen,
  onClose,
  employee
}) => {
  const queryClient = useQueryClient()
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [generateCredentials, setGenerateCredentials] = useState(true)

  const activateUserMutation = useMutation({
    mutationFn: async (data: { 
      sendWelcomeEmail: boolean
      generateCredentials: boolean 
    }) => {
      const response = await api.post<ActivationResponse>(
        `/admin/users/${employee.id}/activate`,
        data
      )
      return response.data
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || 'User activated successfully!')
        
        // Refresh both credentials and employee lists
        // queryClient.invalidateQueries({ queryKey: ['employee-credentials'] })
        // queryClient.invalidateQueries({ queryKey: ['employees'] })
        
        // onClose()
        // setTimeout(() => window.location.reload(), 1000)
        queryClient.invalidateQueries({ queryKey: ['admin-users'] })
        toast.success('User activated successfully!')
        onClose()

      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to activate user')
    }
  })

  const handleActivateUser = () => {
    activateUserMutation.mutate({
      sendWelcomeEmail,
      generateCredentials
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Activate User Account
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Employee Info */}
        <div className="mb-6">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-gray-900">
              {employee.firstName} {employee.lastName}
            </p>
            <p className="text-sm text-gray-600">{employee.employeeNumber}</p>
            <p className="text-sm text-gray-600">{employee.email}</p>
            <p className="text-xs text-gray-500 mt-1">
              Current Status: <span className="capitalize">{employee.employmentStatus}</span>
            </p>
          </div>
        </div>

        {/* Activation Options */}
        <div className="mb-6 space-y-4">
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={generateCredentials}
                onChange={(e) => setGenerateCredentials(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-700">
                  Generate Login Credentials
                </span>
                <p className="text-xs text-gray-500">
                  Create a temporary password for the user to access the system
                </p>
              </div>
            </label>
          </div>

          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={sendWelcomeEmail}
                onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                disabled={!generateCredentials}
              />
              <div className="ml-3">
                <span className={`text-sm font-medium ${
                  generateCredentials ? 'text-gray-700' : 'text-gray-400'
                }`}>
                  Send Welcome Email
                </span>
                <p className="text-xs text-gray-500">
                  Email login credentials and welcome message to the employee
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Account Activation</strong>
              </p>
              <p className="text-xs text-blue-700 mt-1">
                This will change the user's employment status to "active" and 
                {generateCredentials && ' generate login credentials'}
                {sendWelcomeEmail && ', send a welcome email'}.
                The user will be able to access the system immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleActivateUser}
            disabled={activateUserMutation.isPending}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {activateUserMutation.isPending ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="ml-2">Activating...</span>
              </div>
            ) : (
              'Activate Account'
            )}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default ActivateUserModal